import type { EmploymentType, WorkerTier } from '../core/index.js'
import type { CensusResponseRules, HppdTarget, PreferencesWindow, ReleaseRules, SchedulingLimitsCell, SchedulingRules, ShortShiftRules, StaffAlertRules, StaffingPlan, WorkforceRules } from './types.js'

/**
 * The API's defaults for the scheduling rules, for a configuration loaded
 * from an API deployed before the block existed: the user's Full Build
 * Rules v208 where it gives a number, the Shift Bidding Demo otherwise
 * (mirrors `DEFAULT_SCHEDULING_RULES` in the API's facilities module).
 */
const cell = (maxHighPriorityDays: number, hoursMin: number, hoursMax: number, ptoDaysPerPeriod: number | null, maxMediumDayOverrides = 0, firstPtoDayDenied = false): SchedulingLimitsCell => ({ maxHighPriorityDays, hoursMin, hoursMax, ptoDaysPerPeriod, maxMediumDayOverrides, firstPtoDayDenied })
const prn = cell(0, 0, 96, 0)
const goldRow: Record<EmploymentType, SchedulingLimitsCell> = { full_time: cell(4, 128, 160, 5), part_time: cell(2, 64, 96, 5), prn, agency: prn }

/** The census response as the API defaults it (census response plan, C1-C15). */
export const DEFAULT_CENSUS_RESPONSE_RULES: CensusResponseRules = { mode: 'automatic', flexOrder: ['volunteers', 'agency', 'prn', 'tier'], addOrder: ['overtime', 'part_time', 'prn', 'agency'], tieBreak: ['score', 'seniority'], floatFirst: true, flexNoticeHours: 2, maxFlexPerWorkerPerPeriod: 2, recallHours: 24, tellWorkerWhy: 'policy', hppdTolerance: 0.1 }

/** The short shift a fraction of a seat becomes under `short_shift` rounding, as the API defaults it (F5): 4 hours from half a seat, 06:00 for a day or night shift's fraction and 16:00 for an evening's. */
export const DEFAULT_SHORT_SHIFT_RULES: ShortShiftRules = { hours: 4, threshold: 0.5, start: { day: '06:00', evening: '16:00', night: '06:00' } }

/** The workforce plan's numbers as the API defaults them (F6): a 40-hour FTE, a 12% buffer. */
export const DEFAULT_WORKFORCE_RULES: WorkforceRules = { hoursPerFte: 40, bufferPct: 12 }

/** The release guard as the API defaults it (month health plan §4). */
export const DEFAULT_RELEASE_RULES: ReleaseRules = { requireMinimumStaffing: true, requireRequirement: false, maxUnfilledPct: null, minFilledPct: 20, requireFreshRun: true, override: 'staff', mode: 'after_review', reviewDays: 4 }

/** What the facility's staff are told, as the API defaults it (facility operations dashboard plan §5): at once, the 06:30 digest and before a release; no Monday summary. */
export const DEFAULT_STAFF_ALERT_RULES: StaffAlertRules = { atOnce: true, digestAt: '06:30', beforeRelease: true, weekly: false }

export const DEFAULT_SCHEDULING_RULES: SchedulingRules = {
  biddingModel: 'single_window',
  periodsOpenForPreferences: 'current_and_next',
  preferencesCloseDaysBeforeMonth: 28,
  preferencesWindow: null,
  buildAtClose: true,
  draftLocksDaysAfterClose: 5,
  releaseDaysBeforePeriod: 14,
  changeWindowHours: 72,
  shiftTypes: [
    { key: 'day', label: 'Day', start: '07:00', end: '15:00', enabled: true },
    { key: 'evening', label: 'Evening', start: '15:00', end: '23:00', enabled: true },
    { key: 'night', label: 'Night', start: '23:00', end: '07:00', enabled: true },
  ],
  defaultShiftTypeByWeekday: { sat: 'day', sun: 'evening', weekday: 'evening' },
  tiersThatBid: ['gold', 'silver', 'bronze'],
  limits: {
    gold: goldRow,
    silver: goldRow,
    bronze: { full_time: cell(4, 160, 160, 3, 2, true), part_time: cell(2, 80, 80, 3, 2, true), prn, agency: prn },
    probationary: { full_time: cell(0, 160, 160, 0), part_time: cell(0, 80, 80, 0), prn, agency: prn },
  },
  contractualFloor: 'block',
  contractedHoursFromWorker: true,
  maxShiftsPerWeek: 5,
  weeklyOvertimeHours: 40,
  dailyOvertimeHours: 8,
  minRestHours: 8,
  noDayAfterEvening: false,
  maxConsecutiveHours: 16,
  consecutiveDaysBlockedAt: 6,
  spreadEvenly: true,
  lowPriorityDays: 'with_neutral',
  shortSeats: 'take_days_off',
  availabilityTypes: ['part_time', 'prn'],
  assignment: { weekendsFirst: true },
  pto: { againstBalance: true, approval: 'auto' },
  tiebreak: 'seniority',
  coverage: { tradeBoardClosesDaysBefore: 7, swapResponseHours: 4, openBoardOpensDaysBefore: 7, openBoardClosesHoursBefore: 24, openBoardOfferHours: 2, lockHoursBefore: 4, openBoardMode: 'first_come', nudgeEveryHours: 48, urgentRepeatHours: 8, bridgeCalloutHoursBefore: 12, ranShortGraceDays: 3 },
  bridge: { eligibility: 'staff', escalateAfterMinutes: 30, withdrawCutoffHours: 8, maxDaysPerPeriod: { full_time: 4, part_time: 4, prn: null, agency: null } },
  units: [],
  credentials: { requiredByRole: {} },
  minimumStaffing: [],
  substitutions: {},
  staffing: null,
  census: DEFAULT_CENSUS_RESPONSE_RULES,
  release: DEFAULT_RELEASE_RULES,
  workforce: DEFAULT_WORKFORCE_RULES,
}

/** What a facility gets when it switches the bid window on (decision B7): 72 hours from 6:00 AM, 31 days before the month. */
export const DEFAULT_PREFERENCES_WINDOW: PreferencesWindow = { opensDaysBeforeMonth: 31, opensAt: '06:00', hours: 72 }

export const TIERS_IN_ORDER: readonly WorkerTier[] = ['gold', 'silver', 'bronze', 'probationary']

/** A configuration's scheduling rules with every field stated, whatever the API it came from answered. */
export function schedulingRulesOf(config: { scheduling?: (Partial<SchedulingRules> & { maxConsecutiveDays?: number | null }) | null } | null): SchedulingRules {
  // An API deployed before 16 October 2026 answers the most days in a row allowed; the rule is now the run that is refused, one more.
  const { maxConsecutiveDays: legacyMaxDays, ...stored } = config?.scheduling ?? {}
  return {
    ...DEFAULT_SCHEDULING_RULES,
    ...stored,
    consecutiveDaysBlockedAt: stored.consecutiveDaysBlockedAt !== undefined ? stored.consecutiveDaysBlockedAt : legacyMaxDays !== undefined ? (legacyMaxDays === null ? null : legacyMaxDays + 1) : DEFAULT_SCHEDULING_RULES.consecutiveDaysBlockedAt,
    defaultShiftTypeByWeekday: { ...DEFAULT_SCHEDULING_RULES.defaultShiftTypeByWeekday, ...(stored.defaultShiftTypeByWeekday ?? {}) },
    limits: Object.fromEntries(TIERS_IN_ORDER.map((tier) => [tier, { ...DEFAULT_SCHEDULING_RULES.limits[tier], ...(stored.limits?.[tier] ?? {}) }])) as SchedulingRules['limits'],
    assignment: { ...DEFAULT_SCHEDULING_RULES.assignment, ...(stored.assignment ?? {}) },
    pto: { ...DEFAULT_SCHEDULING_RULES.pto, ...(stored.pto ?? {}) },
    coverage: { ...DEFAULT_SCHEDULING_RULES.coverage, ...(stored.coverage ?? {}) },
    bridge: { ...DEFAULT_SCHEDULING_RULES.bridge, ...(stored.bridge ?? {}), maxDaysPerPeriod: { ...DEFAULT_SCHEDULING_RULES.bridge.maxDaysPerPeriod, ...(stored.bridge?.maxDaysPerPeriod ?? {}) } },
    units: stored.units ?? [],
    credentials: { requiredByRole: stored.credentials?.requiredByRole ?? {} },
    minimumStaffing: stored.minimumStaffing ?? [],
    substitutions: stored.substitutions ?? {},
    staffing: stored.staffing ? { ...stored.staffing, shortShift: { ...DEFAULT_SHORT_SHIFT_RULES, ...(stored.staffing.shortShift ?? {}), start: { ...DEFAULT_SHORT_SHIFT_RULES.start, ...(stored.staffing.shortShift?.start ?? {}) } } } : null,
    census: { ...DEFAULT_CENSUS_RESPONSE_RULES, ...(stored.census ?? {}) },
    release: { ...DEFAULT_RELEASE_RULES, ...(stored.release ?? {}) },
    workforce: { ...DEFAULT_WORKFORCE_RULES, ...(stored.workforce ?? {}) },
  }
}

/**
 * An example HPPD target for a skilled nursing facility, labelled as such on the Configuration page: a facility
 * replaces it with its own assessment's numbers. 3.5 total hours per patient day (0.5 RN, 0.6 LPN, 2.4 CNA), 40 / 35 / 25
 * across day, evening and night - the usual CNA split.
 */
export const EXAMPLE_HPPD_TARGET: HppdTarget = { byRole: { RN: 0.5, LPN: 0.6, CNA: 2.4 }, shiftSplit: { day: 40, evening: 35, night: 25 } }

/** What a facility gets when it switches a staffing plan on: the example HPPD target, rounding up, a four-week forecast. */
export const EXAMPLE_STAFFING_PLAN: StaffingPlan = { method: 'hppd', hppd: EXAMPLE_HPPD_TARGET, grid: [], fixed: [], rounding: 'up', shortShift: DEFAULT_SHORT_SHIFT_RULES, acuityFactor: 1, forecastWeeks: 4 }
